import React from "react";
import { calculateNormalizedPower } from "../../utils/normalizedPower";
import { useTranslation } from "../../i18n";

function timeToSeconds(timeStr) {
  if (typeof timeStr !== "string" || !timeStr.includes(":")) return 0;
  const [h, m, s] = timeStr.split(":").map(Number);
  return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.round(seconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const readRecordTimeMs = (r) => {
  if (!r) return null;
  const t =
    r?.timestamp ?? r?.time ?? r?.datetime ?? r?.dateTime ?? r?.createdAt ??
    r?.record?.timestamp ?? r?.record?.time ?? r?.data?.timestamp ?? r?.data?.time ??
    r?.values?.timestamp ?? r?.values?.time ?? null;
  if (t == null) return null;
  if (typeof t === "number" && Number.isFinite(t)) {
    if (t > 1e12) return t;
    if (t > 1e9)  return t * 1000;
  }
  const ms = new Date(t).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const LapsTableCycling = ({ laps = [], records = [], fileId }) => {
  const { t } = useTranslation();

  if (!Array.isArray(laps) || laps.length === 0) return <p></p>;

  let accumulatedSeconds = 0;

  const processedLaps = laps.map((lap) => {
    const startTime = lap?.start_time;
    const lapStart  = startTime ? new Date(startTime).getTime() : null;
    const durSecs   = timeToSeconds(lap?.duracion || "00:00:00");
    const lapEnd    = lapStart != null ? lapStart + durSecs * 1000 : null;

    const lapRecords =
      lapStart != null && lapEnd != null && Array.isArray(records) && records.length > 0
        ? records.filter((r) => {
            const recordMs = readRecordTimeMs(r);
            if (!recordMs) return false;
            return recordMs >= lapStart && recordMs <= lapEnd;
          })
        : [];

    let maxSpeedFromRecords = 0;
    if (lapRecords.length) {
      for (const record of lapRecords) {
        const sp = Number(record?.speed);
        if (Number.isFinite(sp) && sp > 0) {
          const speedKmH = sp * 3.6;
          if (speedKmH > maxSpeedFromRecords) maxSpeedFromRecords = speedKmH;
        }
      }
    }

    const total_timer_time = durSecs;
    const totalDistance    = parseFloat(String(lap?.total_distance ?? lap?.distancia ?? "0").replace(",", "."));
    const distKm           = Number.isFinite(totalDistance) ? totalDistance : 0;
    const avg_speed        = distKm > 0 && total_timer_time > 0 ? (distKm / (total_timer_time / 3600)) : 0;

    const np              = calculateNormalizedPower(lapRecords, { debug: false, fallback: "avg" });
    const normalizedPower = Number.isFinite(np) ? Math.round(np) : 0;

    accumulatedSeconds += durSecs;
    const tiempoAcumulado    = formatTime(accumulatedSeconds);
    const maxSpeedFormatted  = Number.isFinite(maxSpeedFromRecords) ? maxSpeedFromRecords.toFixed(2) : "0.00";
    const avgPower           = typeof lap?.avg_power === "number" ? lap.avg_power.toFixed(0) : "-";
    const maxPower           = typeof lap?.max_power === "number" ? lap.max_power.toFixed(0) : "-";
    const wKg                = typeof lap?.wKg === "string" || typeof lap?.wKg === "number" ? lap.wKg : "-";

    return {
      ...lap,
      total_timer_time, avg_speed, max_speed: maxSpeedFromRecords, normalizedPower,
      tiempoAcumulado, velocidadPromedio: avg_speed.toFixed(2), velocidadMaxima: maxSpeedFormatted,
      avg_power: avgPower, max_power: maxPower, wKg,
      totalDistance: distKm, totalTime: durSecs,
      __lapRecordsCount: lapRecords.length,
    };
  });

  const safeNumber = (val) => {
    if (val === null || val === undefined || val === "-") return 0;
    const num = parseFloat(val);
    return isNaN(num) ? 0 : num;
  };

  const totals = processedLaps.reduce((acc, lap) => {
    acc.duracion                  += timeToSeconds(lap.duracion || "00:00:00");
    acc.distancia                 += safeNumber(lap.distancia);
    acc.calorias                  += safeNumber(lap.calorias);
    acc.ascenso                   += safeNumber(lap.ascenso);
    acc.descenso                  += safeNumber(lap.descenso);
    acc.frecuenciaCardiacaPromedio += safeNumber(lap.frecuenciaCardiacaPromedio);
    acc.frecuenciaCardiacaMaxima   += safeNumber(lap.frecuenciaCardiacaMaxima);
    acc.velocidadPromedio          += safeNumber(lap.velocidadPromedio);
    acc.velocidadMaxima             = Math.max(acc.velocidadMaxima, safeNumber(lap.velocidadMaxima));
    acc.cadenciaPromedio           += safeNumber(lap.cadenciaPromedio);
    acc.temperaturaPromedio        += safeNumber(lap.temperaturaPromedio);
    acc.temperaturaMaxima          += safeNumber(lap.temperaturaMaxima);
    acc.normalizedPower            += safeNumber(lap.normalizedPower);
    acc.avgPower                   += safeNumber(lap.avg_power);
    acc.maxPower                   += safeNumber(lap.max_power);
    acc.wKg                        += safeNumber(lap.wKg);
    return acc;
  }, { duracion: 0, distancia: 0, calorias: 0, ascenso: 0, descenso: 0, frecuenciaCardiacaPromedio: 0, frecuenciaCardiacaMaxima: 0, velocidadPromedio: 0, velocidadMaxima: 0, cadenciaPromedio: 0, temperaturaPromedio: 0, temperaturaMaxima: 0, normalizedPower: 0, avgPower: 0, maxPower: 0, wKg: 0 });

  const totalLaps = processedLaps.length;

  const renderVelocidadMaxima = (valor) => {
    const num   = parseFloat(valor);
    const color = num > 60 ? "#f28b82" : num > 50 ? "#fff200" : "transparent";
    return <td style={{ backgroundColor: color }}>{valor}</td>;
  };

  return (
    <div className="dashboard-container">
      <div className="header" style={{ textAlign: "center", marginTop: "2rem" }}>
        <h2 style={{ fontSize: 14, fontWeight: 700, color: '#193255' }}>{t('laps.dashboardCyclingTitle')}</h2>
      </div>

      <div style={{ overflowX: "auto", marginTop: "2rem" }}>
        <table className="laps-table">
          <thead>
            <tr>
              <th>{t('laps.lap')}</th>
              <th>{t('laps.duration')}</th>
              <th>{t('laps.accumulatedTime')}</th>
              <th>{t('laps.distanceKm')}</th>
              <th>{t('laps.calories')}</th>
              <th>{t('laps.ascentM')}</th>
              <th>{t('laps.descentM')}</th>
              <th>{t('laps.avgHRShort')}</th>
              <th>{t('laps.maxHRShort')}</th>
              <th>{t('laps.avgSpeedShort')}</th>
              <th>{t('laps.maxSpeedShort')}</th>
              <th>{t('laps.avgCadenceShort')}</th>
              <th>{t('laps.avgTempShort')}</th>
              <th>{t('laps.maxTempShort')}</th>
              <th>{t('laps.np')}</th>
              <th>{t('laps.avgPower')}</th>
              <th>{t('laps.maxPower')}</th>
              <th>{t('laps.wkg')}</th>
            </tr>
          </thead>

          <tbody>
            {processedLaps.map((lap, index) => (
              <tr key={lap.vuelta || index}>
                <td>{lap.vuelta}</td>
                <td>{lap.duracion}</td>
                <td>{lap.tiempoAcumulado}</td>
                <td>{lap.distancia}</td>
                <td>{lap.calorias}</td>
                <td>{lap.ascenso}</td>
                <td>{lap.descenso}</td>
                <td>{lap.frecuenciaCardiacaPromedio}</td>
                <td>{lap.frecuenciaCardiacaMaxima}</td>
                <td>{lap.velocidadPromedio}</td>
                {renderVelocidadMaxima(lap.velocidadMaxima)}
                <td>{lap.cadenciaPromedio}</td>
                <td>{lap.temperaturaPromedio}</td>
                <td>{lap.temperaturaMaxima}</td>
                <td>{lap.normalizedPower}</td>
                <td>{lap.avg_power}</td>
                <td>{lap.max_power}</td>
                <td>{lap.wKg}</td>
              </tr>
            ))}

            {totalLaps > 0 && (
              <tr className="totals-row" style={{ backgroundColor: "#e3f2fd", fontWeight: "bold" }}>
                <td>{t('laps.total')}</td>
                <td>{formatTime(totals.duracion)}</td>
                <td>--</td>
                <td>{!isNaN(totals.distancia)  ? totals.distancia.toFixed(2)  : "-"}</td>
                <td>{!isNaN(totals.calorias)   ? totals.calorias.toFixed(0)   : "-"}</td>
                <td>{!isNaN(totals.ascenso)    ? totals.ascenso.toFixed(0)    : "-"}</td>
                <td>{!isNaN(totals.descenso)   ? totals.descenso.toFixed(0)   : "-"}</td>
                <td>{!isNaN(totals.frecuenciaCardiacaPromedio / totalLaps) ? (totals.frecuenciaCardiacaPromedio / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.frecuenciaCardiacaMaxima  / totalLaps) ? (totals.frecuenciaCardiacaMaxima  / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.velocidadPromedio / totalLaps) ? (totals.velocidadPromedio / totalLaps).toFixed(2) : "-"}</td>
                {renderVelocidadMaxima(typeof totals.velocidadMaxima === "number" && !isNaN(totals.velocidadMaxima) ? totals.velocidadMaxima.toFixed(2) : "-")}
                <td>{!isNaN(totals.cadenciaPromedio    / totalLaps) ? (totals.cadenciaPromedio    / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.temperaturaPromedio / totalLaps) ? (totals.temperaturaPromedio / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.temperaturaMaxima   / totalLaps) ? (totals.temperaturaMaxima   / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.normalizedPower / totalLaps) ? (totals.normalizedPower / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.avgPower       / totalLaps) ? (totals.avgPower       / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.maxPower       / totalLaps) ? (totals.maxPower       / totalLaps).toFixed(0) : "-"}</td>
                <td>{!isNaN(totals.wKg            / totalLaps) ? (totals.wKg            / totalLaps).toFixed(2) : "-"}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LapsTableCycling;
